import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db } from '../utils/firebase';
import { collection, getDocs } from 'firebase/firestore';

/**
 * BackupDashboard - OWNER ONLY dashboard for backup and restore operations
 * 
 * Secret URL: /owner-backup-7k3m
 * Only accessible by authorized users
 */

const ALLOWED_UIDS = [
  'mygxGSMYBWZd9v02Xyew6WMtYe13', // Anthony
  'uM4lKlr7SjPGFzDK064F1Dk6Aco2'  // Additional authorized user
];

const BackupDashboard = () => {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  
  // Selected club for viewing backups
  const [selectedClub, setSelectedClub] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  
  // Operation states
  const [backingUp, setBackingUp] = useState(null); // clubId being backed up
  const [restoring, setRestoring] = useState(null); // filename being restored
  const [message, setMessage] = useState(null);
  
  const navigate = useNavigate();
  const functions = getFunctions(undefined, 'australia-southeast1');

  // Check authorization
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || !ALLOWED_UIDS.includes(user.uid)) {
        navigate('/');
        return;
      }
      setAuthorized(true);
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, [navigate]);

  // Load all clubs
  useEffect(() => {
    if (!authorized) return;
    
    const loadClubs = async () => {
      try {
        const clubsSnapshot = await getDocs(collection(db, 'clubs'));
        const clubsList = clubsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || 'Unknown Club',
          subscriptionStatus: doc.data().subscriptionStatus || 'unknown'
        }));
        clubsList.sort((a, b) => a.name.localeCompare(b.name));
        setClubs(clubsList);
      } catch (error) {
        console.error('Error loading clubs:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadClubs();
  }, [authorized]);

  // Load backups for selected club
  const loadBackups = async (clubId) => {
    setLoadingBackups(true);
    setBackups([]);
    try {
      const listClubBackups = httpsCallable(functions, 'listClubBackups');
      const result = await listClubBackups({ clubId });
      setBackups(result.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
      setMessage({ type: 'error', text: `Failed to load backups: ${error.message}` });
    } finally {
      setLoadingBackups(false);
    }
  };

  // Select a club
  const handleSelectClub = (club) => {
    setSelectedClub(club);
    setMessage(null);
    loadBackups(club.id);
  };

  // Backup a club now
  const handleBackupNow = async (clubId, clubName) => {
    if (!window.confirm(`Create a manual backup for "${clubName}" now?`)) return;
    
    setBackingUp(clubId);
    setMessage(null);
    try {
      const backupClubNow = httpsCallable(functions, 'backupClubNow');
      const result = await backupClubNow({ clubId });
      setMessage({ type: 'success', text: `✅ Backup created: ${result.data.filename}` });
      
      // Refresh backups list if this club is selected
      if (selectedClub?.id === clubId) {
        loadBackups(clubId);
      }
    } catch (error) {
      console.error('Backup failed:', error);
      setMessage({ type: 'error', text: `❌ Backup failed: ${error.message}` });
    } finally {
      setBackingUp(null);
    }
  };

  // Restore from backup
  const handleRestore = async (clubId, backupFilename) => {
    const backupDate = backupFilename.split('/').pop().replace('.json', '');
    
    if (!window.confirm(
      `⚠️ RESTORE WARNING\n\n` +
      `This will restore "${selectedClub.name}" to the backup from:\n${backupDate}\n\n` +
      `Current data will be OVERWRITTEN.\n\n` +
      `Are you absolutely sure?`
    )) return;
    
    // Double confirm
    if (!window.confirm(`FINAL CONFIRMATION: Restore ${selectedClub.name}?`)) return;
    
    setRestoring(backupFilename);
    setMessage(null);
    try {
      const restoreClubFromBackup = httpsCallable(functions, 'restoreClubFromBackup');
      await restoreClubFromBackup({ clubId, backupFilename });
      setMessage({ type: 'success', text: `✅ Successfully restored ${selectedClub.name} from ${backupDate}` });
    } catch (error) {
      console.error('Restore failed:', error);
      setMessage({ type: 'error', text: `❌ Restore failed: ${error.message}` });
    } finally {
      setRestoring(null);
    }
  };

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '—';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract backup date from filename
  const getBackupDateFromFilename = (filename) => {
    const parts = filename.split('/');
    const file = parts[parts.length - 1];
    return file.replace('.json', '').replace('manual-', 'Manual: ');
  };

  if (!authChecked) {
    return (
      <div style={styles.loadingScreen}>
        Loading...
      </div>
    );
  }

  if (!authorized) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🗄️ Backup & Restore</h1>
          <p style={styles.subtitle}>
            Manage club backups • Restore individual clubs without affecting others
          </p>
        </div>
        <button 
          style={styles.backButton}
          onClick={() => navigate('/owner-9x7kq2m')}
        >
          ← Back to Onboarding
        </button>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
          borderColor: message.type === 'success' ? '#22c55e' : '#ef4444',
          color: message.type === 'success' ? '#166534' : '#dc2626'
        }}>
          {message.text}
        </div>
      )}

      <div style={styles.mainContent}>
        {/* Club List */}
        <div style={styles.clubListPanel}>
          <h2 style={styles.panelTitle}>Clubs ({clubs.length})</h2>
          
          {loading ? (
            <div style={styles.loadingText}>Loading clubs...</div>
          ) : (
            <div style={styles.clubList}>
              {clubs.map(club => (
                <div
                  key={club.id}
                  style={{
                    ...styles.clubItem,
                    backgroundColor: selectedClub?.id === club.id ? '#e0f2fe' : '#f8fafc',
                    borderColor: selectedClub?.id === club.id ? '#0ea5e9' : '#e2e8f0'
                  }}
                  onClick={() => handleSelectClub(club)}
                >
                  <div style={styles.clubInfo}>
                    <div style={styles.clubName}>{club.name}</div>
                    <div style={styles.clubId}>{club.id}</div>
                  </div>
                  <button
                    style={{
                      ...styles.backupBtn,
                      opacity: backingUp === club.id ? 0.5 : 1
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBackupNow(club.id, club.name);
                    }}
                    disabled={backingUp === club.id}
                  >
                    {backingUp === club.id ? '...' : '📦'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Backup Details Panel */}
        <div style={styles.backupPanel}>
          {!selectedClub ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>👈</div>
              <div style={styles.emptyText}>Select a club to view backups</div>
            </div>
          ) : (
            <>
              <div style={styles.panelHeader}>
                <h2 style={styles.panelTitle}>{selectedClub.name}</h2>
                <button
                  style={styles.refreshBtn}
                  onClick={() => loadBackups(selectedClub.id)}
                  disabled={loadingBackups}
                >
                  ↻ Refresh
                </button>
              </div>
              
              <div style={styles.clubIdDisplay}>
                Club ID: <code style={styles.code}>{selectedClub.id}</code>
              </div>

              {loadingBackups ? (
                <div style={styles.loadingText}>Loading backups...</div>
              ) : backups.length === 0 ? (
                <div style={styles.noBackups}>
                  <div style={styles.noBackupsIcon}>📭</div>
                  <div>No backups found for this club</div>
                  <button
                    style={styles.createBackupBtn}
                    onClick={() => handleBackupNow(selectedClub.id, selectedClub.name)}
                    disabled={backingUp === selectedClub.id}
                  >
                    {backingUp === selectedClub.id ? 'Creating...' : 'Create First Backup'}
                  </button>
                </div>
              ) : (
                <div style={styles.backupList}>
                  <div style={styles.backupCount}>
                    {backups.length} backup{backups.length !== 1 ? 's' : ''} available
                  </div>
                  
                  {backups.map((backup, index) => (
                    <div key={backup.filename} style={styles.backupItem}>
                      <div style={styles.backupInfo}>
                        <div style={styles.backupDate}>
                          {index === 0 && <span style={styles.latestBadge}>LATEST</span>}
                          {getBackupDateFromFilename(backup.filename)}
                        </div>
                        <div style={styles.backupMeta}>
                          {formatDate(backup.created)} • {formatSize(backup.size)}
                        </div>
                      </div>
                      <button
                        style={{
                          ...styles.restoreBtn,
                          opacity: restoring === backup.filename ? 0.5 : 1
                        }}
                        onClick={() => handleRestore(selectedClub.id, backup.filename)}
                        disabled={restoring === backup.filename}
                      >
                        {restoring === backup.filename ? 'Restoring...' : '⏮️ Restore'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Info Footer */}
      <div style={styles.infoFooter}>
        <div style={styles.infoItem}>
          <strong>📦 Daily Backups:</strong> Run automatically at 2am Sydney time
        </div>
        <div style={styles.infoItem}>
          <strong>🗑️ Retention:</strong> Backups kept for 30 days
        </div>
        <div style={styles.infoItem}>
          <strong>⚠️ Restore:</strong> Only affects the selected club - other clubs untouched
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f1f5f9',
    padding: '24px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  },
  loadingScreen: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#6b7280'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#243665',
    margin: '0 0 4px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  backButton: {
    padding: '10px 16px',
    backgroundColor: '#64748b',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  message: {
    padding: '16px 20px',
    borderRadius: '8px',
    border: '1px solid',
    marginBottom: '24px',
    fontSize: '14px',
    fontWeight: '500'
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '350px 1fr',
    gap: '24px',
    marginBottom: '24px'
  },
  clubListPanel: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    maxHeight: 'calc(100vh - 280px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  panelTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 16px 0'
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  clubList: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  clubItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    borderRadius: '8px',
    border: '2px solid',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  clubInfo: {
    flex: 1,
    minWidth: 0
  },
  clubName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  clubId: {
    fontSize: '11px',
    color: '#94a3b8',
    fontFamily: 'monospace'
  },
  backupBtn: {
    padding: '6px 10px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    marginLeft: '8px'
  },
  backupPanel: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    maxHeight: 'calc(100vh - 280px)',
    overflow: 'auto'
  },
  clubIdDisplay: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '20px'
  },
  code: {
    backgroundColor: '#f1f5f9',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '12px'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#94a3b8'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px'
  },
  emptyText: {
    fontSize: '16px'
  },
  loadingText: {
    padding: '40px',
    textAlign: 'center',
    color: '#94a3b8'
  },
  noBackups: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#64748b'
  },
  noBackupsIcon: {
    fontSize: '40px',
    marginBottom: '12px'
  },
  createBackupBtn: {
    marginTop: '16px',
    padding: '10px 20px',
    backgroundColor: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  refreshBtn: {
    padding: '6px 12px',
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  backupList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  backupCount: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '8px'
  },
  backupItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0'
  },
  backupInfo: {
    flex: 1
  },
  backupDate: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  latestBadge: {
    backgroundColor: '#22c55e',
    color: 'white',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '700'
  },
  backupMeta: {
    fontSize: '12px',
    color: '#64748b'
  },
  restoreBtn: {
    padding: '8px 16px',
    backgroundColor: '#fef3c7',
    border: '1px solid #fbbf24',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#92400e'
  },
  infoFooter: {
    display: 'flex',
    gap: '24px',
    padding: '16px 20px',
    backgroundColor: '#e0f2fe',
    borderRadius: '8px',
    flexWrap: 'wrap'
  },
  infoItem: {
    fontSize: '13px',
    color: '#0369a1'
  }
};

export default BackupDashboard;
